import { call, put, takeLatest } from "redux-saga/effects";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
    EXPORT_PRE_EMBAUCHE_DOCUMENT_PDF_REQUEST,
    exportPreEmbaucheDocumentPdfFail,
    exportPreEmbaucheDocumentPdfSuccess,
    FETCH_PRE_EMBAUCHE_DOCUMENT_REQUEST,
    fetchPreEmbaucheDocumentFail,
    fetchPreEmbaucheDocumentSuccess,
    GENERATE_PRE_EMBAUCHE_DOCUMENT_REQUEST,
    generatePreEmbaucheDocumentFail,
    generatePreEmbaucheDocumentSuccess,
    setPreEmbaucheError,
    setPreEmbaucheLoading,
    UPDATE_PRE_EMBAUCHE_DOCUMENT_REQUEST,
    updatePreEmbaucheDocumentFail,
    updatePreEmbaucheDocumentSuccess,
    UPDATE_PRE_EMBAUCHE_PROCEDURE_STEP_REQUEST,
    updatePreEmbaucheProcedureStepFail,
    updatePreEmbaucheProcedureStepSuccess,
} from "../actions/preEmbaucheActions";
import { API_BASE_URL, extractErrorMessage, getRequest, getRequestForPdf, postRequest, putRequest } from "../../../api";

const getUid = () => sessionStorage.getItem("uid");
const getToken = () => sessionStorage.getItem("token");

const DOCUMENT_CONFIG = {
    lettreConfirmation: {
        segment: "lettre-confirmation",
        label: "Lettre de confirmation",
        filenamePrefix: "Lettre_confirmation",
    },
    contratTravail: {
        segment: "contrat-travail",
        label: "Contrat de travail",
        filenamePrefix: "Contrat_travail",
    },
    procedureIntegration: {
        segment: "procedure-integration",
        label: "Procedure_integration",
        filenamePrefix: "Procedure_integration",
    },
};

const buildLoadingKey = (documentType, candidatureId, operation) =>
    `${documentType}:${candidatureId}:${operation}`;

const isMockableStatus = (status) => [404, 405, 409, 500, 501].includes(Number(status));

const cloneDocument = (document, overrides = {}) => ({
    ...(document || {}),
    ...overrides,
});

const toDisplayDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("fr-FR");
};

const ensureFilename = (documentType, candidatureId) => {
    const config = DOCUMENT_CONFIG[documentType];
    return `${config?.filenamePrefix || "document"}_${candidatureId}.pdf`;
};

const extractFilename = (contentDisposition, fallback) => {
    if (!contentDisposition) return fallback;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

    const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return basicMatch?.[1] || fallback;
};

const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const writeWrappedText = (pdf, text, x, startY, maxWidth, lineHeight = 7) => {
    const lines = pdf.splitTextToSize(text || "", maxWidth);
    pdf.text(lines, x, startY);
    return startY + lines.length * lineHeight;
};

const exportLetterPdfLocally = (documentData, candidatureId) => {
    const pdf = new jsPDF();
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("Lettre de confirmation de recrutement", 14, 18);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);

    let cursorY = 28;
    cursorY += 5;
    pdf.setFont("helvetica", "bold");
    pdf.text("Contenu", 14, cursorY);
    cursorY += 8;
    pdf.setFont("helvetica", "normal");
    writeWrappedText(pdf, documentData.content, 14, cursorY, 180);

    pdf.save(ensureFilename("lettreConfirmation", candidatureId));
};

const exportContractPdfLocally = (documentData, candidatureId) => {
    const pdf = new jsPDF();
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("Contrat de travail", 14, 18);

    autoTable(pdf, {
        startY: 28,
        styles: { fontSize: 10 },
        head: [["Champ", "Valeur"]],
        body: [
            ["Candidat", documentData.candidatNomComplet || "-"],
            ["Poste", documentData.poste || "-"],
            ["Manager", documentData.manager || "-"],
            ["Email", documentData.email || "-"],
            ["Telephone", documentData.telephone || "-"],
            ["Type de contrat", documentData.typeContrat || "-"],
            ["Date de debut", toDisplayDate(documentData.dateDebut)],
            ["Date de fin (CDD)", toDisplayDate(documentData.dateFin)],
            ["Periode d'essai", documentData.periodeEssai || "-"],
            ["Salaire", documentData.salaire || "-"],
            ["Lieu de travail", documentData.lieuTravail || "-"],
            ["Regime de travail", documentData.regimeTravail || documentData.horairesTravail || "-"],
            ["Commentaire RH", documentData.commentaireRh || "-"],
            ["Statut", documentData.statusLabel || documentData.status || "-"],
        ],
    });

    if (documentData.contenu) {
        let cursorY = pdf.lastAutoTable?.finalY ? pdf.lastAutoTable.finalY + 12 : 120;
        pdf.setFont("helvetica", "bold");
        pdf.text("Contenu du contrat", 14, cursorY);
        cursorY += 8;
        pdf.setFont("helvetica", "normal");
        writeWrappedText(pdf, documentData.contenu, 14, cursorY, 180);
    }

    pdf.save(ensureFilename("contratTravail", candidatureId));
};

const exportDocumentLocally = (documentType, documentData, candidatureId) => {
    switch (documentType) {
        case "lettreConfirmation":
            exportLetterPdfLocally(documentData, candidatureId);
            return;
        case "contratTravail":
            exportContractPdfLocally(documentData, candidatureId);
            return;
        default:
            throw new Error("Type de document non pris en charge");
    }
};

export function* fetchPreEmbaucheDocumentSaga(action) {
    const { documentType, candidatureId } = action.payload;
    const config = DOCUMENT_CONFIG[documentType];
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey(documentType, candidatureId, "fetch");
    const url = `${API_BASE_URL}/api/pre-embauche/${candidatureId}/${config.segment}`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        const resp = yield call(getRequest, { url, uid, token });
        yield put(fetchPreEmbaucheDocumentSuccess(documentType, candidatureId, resp.data));
    } catch (error) {
        if (isMockableStatus(error?.response?.status)) {
            yield put(fetchPreEmbaucheDocumentSuccess(documentType, candidatureId, null));
        } else {
            const message = extractErrorMessage(error, `Erreur chargement ${config.label.toLowerCase()}`);
            yield put(fetchPreEmbaucheDocumentFail(documentType, candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export function* generatePreEmbaucheDocumentSaga(action) {
    const { documentType, candidatureId, document } = action.payload;
    const config = DOCUMENT_CONFIG[documentType];
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey(documentType, candidatureId, "generate");
    const url = `${API_BASE_URL}/api/pre-embauche/${candidatureId}/${config.segment}/generate`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        const resp = yield call(postRequest, { url, data: document, uid, token });
        yield put(generatePreEmbaucheDocumentSuccess(documentType, candidatureId, resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: `${config.label} generee`,
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        if (isMockableStatus(error?.response?.status)) {
            const mocked = cloneDocument(document, { isMocked: true, source: "mock" });
            yield put(generatePreEmbaucheDocumentSuccess(documentType, candidatureId, mocked));
            yield call([Swal, Swal.fire], {
                icon: "info",
                text: `${config.label} generee en mode brouillon local`,
                timer: 1800,
                showConfirmButton: false,
            });
        } else {
            const message = extractErrorMessage(error, `Erreur generation ${config.label.toLowerCase()}`);
            yield put(generatePreEmbaucheDocumentFail(documentType, candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
            yield call([Swal, Swal.fire], {
                icon: "error",
                text: message,
                timer: 2300,
                showConfirmButton: false,
            });
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export function* updatePreEmbaucheDocumentSaga(action) {
    const { documentType, candidatureId, document } = action.payload;
    const config = DOCUMENT_CONFIG[documentType];
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey(documentType, candidatureId, "save");
    const url = `${API_BASE_URL}/api/pre-embauche/${candidatureId}/${config.segment}`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        const resp = yield call(putRequest, { url, data: document, uid, token });
        yield put(updatePreEmbaucheDocumentSuccess(documentType, candidatureId, resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: `${config.label} enregistree`,
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        if (isMockableStatus(error?.response?.status)) {
            const mocked = cloneDocument(document, { isMocked: true, source: "mock" });
            yield put(updatePreEmbaucheDocumentSuccess(documentType, candidatureId, mocked));
            yield call([Swal, Swal.fire], {
                icon: "info",
                text: `${config.label} conservée `,
                timer: 1800,
                showConfirmButton: false,
            });
        } else {
            const message = extractErrorMessage(error, `Erreur enregistrement ${config.label.toLowerCase()}`);
            yield put(updatePreEmbaucheDocumentFail(documentType, candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
            yield call([Swal, Swal.fire], {
                icon: "error",
                text: message,
                timer: 2300,
                showConfirmButton: false,
            });
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export function* updatePreEmbaucheProcedureStepSaga(action) {
    const { candidatureId, stepId, step, document } = action.payload;
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey("procedureIntegration", candidatureId, "step");
    const url = `${API_BASE_URL}/api/pre-embauche/procedure-integration/steps/${stepId}`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        yield call(putRequest, { url, data: step, uid, token });
        yield put(updatePreEmbaucheProcedureStepSuccess(candidatureId, document));
    } catch (error) {
        if (isMockableStatus(error?.response?.status)) {
            yield put(updatePreEmbaucheProcedureStepSuccess(candidatureId, cloneDocument(document, { isMocked: true, source: "mock" })));
        } else {
            const message = extractErrorMessage(error, "Erreur mise a jour etape integration");
            yield put(updatePreEmbaucheProcedureStepFail(candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
            yield call([Swal, Swal.fire], {
                icon: "error",
                text: message,
                timer: 2300,
                showConfirmButton: false,
            });
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export function* exportPreEmbaucheDocumentPdfSaga(action) {
    const { documentType, candidatureId, document } = action.payload;
    const config = DOCUMENT_CONFIG[documentType];
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey(documentType, candidatureId, "export");
    const url = `${API_BASE_URL}/api/pre-embauche/${candidatureId}/${config.segment}/pdf`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        const resp = yield call(getRequestForPdf, {
            url,
            token,
            headers: { uid },
            responseType: "arraybuffer",
        });

        downloadBlob(
            resp.data,
            extractFilename(resp.headers?.["content-disposition"], ensureFilename(documentType, candidatureId))
        );

        yield put(exportPreEmbaucheDocumentPdfSuccess(documentType, candidatureId, document));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: `${config.label} exportee en PDF`,
            timer: 1800,
            showConfirmButton: false,
        });
    } catch (error) {
        try {
            exportDocumentLocally(documentType, document, candidatureId);
            yield put(exportPreEmbaucheDocumentPdfSuccess(documentType, candidatureId, cloneDocument(document, { isMocked: true, source: "mock" })));

            yield call([Swal, Swal.fire], {
                icon: "info",
                text: `${config.label} exportée  en PDF`,
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (localError) {
            const message = extractErrorMessage(error, `Erreur export PDF ${config.label.toLowerCase()}`);
            yield put(exportPreEmbaucheDocumentPdfFail(documentType, candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
            yield call([Swal, Swal.fire], {
                icon: "error",
                text: message,
                timer: 2300,
                showConfirmButton: false,
            });
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export default function* preEmbaucheSaga() {
    yield takeLatest(FETCH_PRE_EMBAUCHE_DOCUMENT_REQUEST, fetchPreEmbaucheDocumentSaga);
    yield takeLatest(GENERATE_PRE_EMBAUCHE_DOCUMENT_REQUEST, generatePreEmbaucheDocumentSaga);
    yield takeLatest(UPDATE_PRE_EMBAUCHE_DOCUMENT_REQUEST, updatePreEmbaucheDocumentSaga);
    yield takeLatest(EXPORT_PRE_EMBAUCHE_DOCUMENT_PDF_REQUEST, exportPreEmbaucheDocumentPdfSaga);
    yield takeLatest(UPDATE_PRE_EMBAUCHE_PROCEDURE_STEP_REQUEST, updatePreEmbaucheProcedureStepSaga);
}
